import { useEffect, useState } from 'react';
import { usePageParam } from '../../hooks/usePageParam';
import { useQuery, useMutation } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Pagination from '../../components/common/Pagination';
import ActionsMenu from '../../components/common/ActionsMenu';
import { ADMIN_DELETED_FILES_QUERY } from '../../graphql/queries';
import { ADMIN_PURGE_FILE_MUTATION, ADMIN_RESTORE_FILE_MUTATION } from '../../graphql/mutations';
import { removalText } from '../../utils/trash';
import styles from './AdminDeletedFiles.module.css';
import Icon, { type IconName } from '../../components/common/Icon';

interface DeletedFile {
  id: string;
  originalName: string;
  extension: string;
  fileSize: number;
  type: 'two_d' | 'three_d' | 'point_cloud' | 'document' | 'indoor' | 'gaussian' | 'orthomosaic' | 'video360';
  status: string;
  deletedAt: string;
  deletedWithProject: boolean;
  deletedBy: { id: string; firstName: string; lastName: string; email: string } | null;
  removesAt: string | null;
  removing: boolean;
  purgedAt: string | null;
  project: { id: string; name: string } | null;
  uploadedBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  updatedAt: string;
}

const TYPE_LABELS: Record<string, { label: string; icon: IconName }> = {
  two_d:       { label: '2D',  icon: 'description' },
  three_d:     { label: '3D',  icon: 'view_in_ar' },
  point_cloud: { label: 'PC',  icon: 'point_scan' },
  document:    { label: 'DOC', icon: 'article' },
  indoor:      { label: 'IN',  icon: 'location_city' },
  gaussian:    { label: 'GS',  icon: 'blur_on' },
  orthomosaic: { label: 'ORTHO', icon: 'satellite_alt' },
  video360:    { label: '360', icon: 'panorama_photosphere' },
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (s: string) => new Date(s).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const PAGE_SIZE = 50;

const AdminDeletedFiles = () => {
  const [search, setSearch]     = useState('');
  const [page, setPage]         = usePageParam();
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [purgingId, setPurgingId] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState('');
  const [now] = useState(() => Date.now());

  const { data, loading, error, refetch } = useQuery(ADMIN_DELETED_FILES_QUERY, {
    variables: { page, limit: PAGE_SIZE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
  });

  const [restoreFile] = useMutation(ADMIN_RESTORE_FILE_MUTATION, {
    onCompleted: () => { setRestoringId(null); refetch(); },
    onError:     (e) => { setRestoringId(null); setRestoreError(`Could not restore the file: ${e.message}`); },
  });

  const [purgeFile] = useMutation(ADMIN_PURGE_FILE_MUTATION, {
    onCompleted: () => { setPurgingId(null); refetch(); },
    onError:     (e) => { setPurgingId(null); setRestoreError(`Could not remove the file: ${e.message}`); refetch(); },
  });

  const handlePurge = (file: DeletedFile) => {
    if (!window.confirm(`Remove "${file.originalName}" from storage for good? It can never be restored.`)) return;
    setRestoreError('');
    setPurgingId(file.id);
    purgeFile({ variables: { fileId: file.id } });
  };

  const handleRestore = (fileId: string) => {
    setRestoreError('');
    setRestoringId(fileId);
    restoreFile({ variables: { fileId } });
  };

  const jobs: DeletedFile[] = data?.adminDeletedFiles?.jobs ?? [];
  const total: number       = data?.adminDeletedFiles?.total ?? 0;
  const totalPages          = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Restoring the last file of the last page would leave an empty page.
  useEffect(() => { if (data && page > totalPages) setPage(totalPages); }, [data, page, totalPages]);

  return (
    <DashboardLayout onSearch={(q) => { setSearch(q); setPage(1); }} searchPlaceholder="Search by file name...">
      <div className="space-y-6">
        <div className="flex justify-between items-end flex-wrap gap-3">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Deleted Files</h2>
            <p className="text-on-surface-variant mt-1">
              Files in Trash across the platform. Restore returns one to its project until it is removed for good
              (Platform Settings → Trash); Delete now removes it from storage at once.
            </p>
          </div>
          <div className={styles.totalBadge}>
            <Icon name="delete_sweep" className="text-base" />
            {total} deleted file{total !== 1 ? 's' : ''}
          </div>
        </div>

        {restoreError && <p className={styles.errorBanner} role="alert">{restoreError}</p>}

        <div className="bg-white rounded-xl overflow-hidden">
          {loading && jobs.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">
              <Icon name="refresh" className="animate-spin mr-2" />
              Loading deleted files...
            </div>
          ) : error && jobs.length === 0 ? (
            <div className={styles.loadError} role="alert">
              <Icon name="error" />
              Could not load deleted files: {error.message}
            </div>
          ) : jobs.length === 0 ? (
            <div className="py-16 text-center">
              <Icon name="delete_outline" className="text-4xl text-outline mb-3 block" />
              <p className="text-on-surface-variant">No deleted files</p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">File</th>
                    <th className="table-header">Type</th>
                    <th className="table-header">Size</th>
                    <th className="table-header">Project</th>
                    <th className="table-header">Deleted</th>
                    <th className="table-header">Deleted by</th>
                    <th className="table-header">Removed for good</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {jobs.map((file) => {
                    const typeInfo = TYPE_LABELS[file.type] ?? { label: file.type, icon: 'insert_drive_file' };
                    return (
                      <tr key={file.id} className="table-row group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Icon name={typeInfo.icon} className="text-base text-on-surface-variant" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-on-surface truncate max-w-[260px]" title={file.originalName}>
                                {file.originalName}
                              </p>
                              <p className="text-[10px] text-on-surface-variant uppercase">.{file.extension}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[10px] font-bold uppercase text-on-surface-variant bg-surface-container px-2 py-0.5 rounded">
                            {typeInfo.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-on-surface-variant">
                          {formatSize(file.fileSize)}
                        </td>
                        <td className="px-6 py-4 text-xs text-on-surface-variant truncate max-w-[200px]" title={file.project?.name ?? '—'}>
                          {file.project?.name ?? '—'}
                        </td>
                        <td className="px-6 py-4 text-xs text-on-surface-variant">
                          {formatDate(file.deletedAt)}
                          {file.deletedWithProject && <span className={styles.withProject}>with its project</span>}
                        </td>
                        <td className="px-6 py-4 text-xs">
                          {file.deletedBy ? (
                            <>
                              <span className={styles.deleterEmail} title={file.deletedBy.email}>{file.deletedBy.email}</span>
                              <span className={styles.deleterName}>{`${file.deletedBy.firstName} ${file.deletedBy.lastName}`.trim()}</span>
                            </>
                          ) : (
                            <span className="text-on-surface-variant" title="Deleted before deletions were recorded, or by an account that no longer exists">Not recorded</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs" title={file.removesAt ? formatDate(file.removesAt) : undefined}>
                          {file.purgedAt
                            ? <span className={styles.purged}>Removed {formatDate(file.purgedAt)}</span>
                            : restoringId === file.id || purgingId === file.id
                              ? (
                                <span className={styles.progress} role="status">
                                  <Icon name="refresh" aria-hidden="true" className="text-sm animate-spin" />
                                  {restoringId === file.id ? 'Restoring…' : 'Deleting…'}
                                </span>
                              )
                              : <span className={styles.removal}>{removalText(file.removesAt, file.removing, now)}</span>}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {!file.purgedAt && (
                            <ActionsMenu
                              label={`Actions for ${file.originalName}`}
                              actions={[
                                {
                                  label: 'Restore', icon: 'restore_from_trash', disabled: restoringId === file.id || file.removing,
                                  hint: file.deletedWithProject ? 'Restore its project in All Projects' : 'Restore file to its original project',
                                  onSelect: () => handleRestore(file.id),
                                },
                                {
                                  label: 'Delete now', icon: 'delete_forever', danger: true, disabled: purgingId === file.id || file.removing,
                                  hint: 'Remove the file from storage for good',
                                  onSelect: () => handlePurge(file),
                                },
                              ]}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>

              <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="deleted files" />
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDeletedFiles;
